"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import NavList from "../NavList";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { Toast, ToastContainer } from "react-bootstrap";
import { useUserStore } from "../../../store/user-store";
import { API_BASE_URL } from "../../../config";

interface FormData {
  bankCertificate: string;
  identityDocument: string;
  rutDocument: string;
}

const Documents = () => {
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore(state => state.setUser);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    setError,
    clearErrors,
  } = useForm<FormData>();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success");

  const [identityFileName, setIdentityFileName] = useState<string>(user?.identityDocument || "");
  const [rutFileName, setRutFileName] = useState<string>(user?.rutDocument || "");
  const [bankCertificateFileName, setBankCertificateFileName] = useState<string>(user?.bankCertificate || "");

  const { postData, patchData } = useDataFetch();

  const { mutateAsync: uploadFile } = postData(
    "/users/upload-file",
    {},
    {
      onSuccess: (response, variables) => {
        const { fieldName } = variables;
        if (response.message === "file_create_success") {
          setValue(fieldName, response.data.fileName);
          trigger(fieldName);
          setToastMessage("Archivo subido exitosamente");
          setToastVariant("success");
          setShowToast(true);
          clearErrors(fieldName);

          if (fieldName === "identityDocument") setIdentityFileName(response.data.fileName);
          if (fieldName === "rutDocument") setRutFileName(response.data.fileName);
          if (fieldName === "bankCertificate") setBankCertificateFileName(response.data.fileName);
        } else {
          setToastMessage("Error al subir el archivo");
          setToastVariant("danger");
          setShowToast(true);
        }
      },
      onError: () => {
        setToastMessage("Error al subir el archivo");
        setToastVariant("danger");
        setShowToast(true);
      },
    }
  );

  const saveDocumentsMutation = patchData(
    "/users/documents/save",
    {},
    {
      onSuccess: () => {
        setToastMessage("Documentos guardados correctamente");
        setToastVariant("success");
        setShowToast(true);
      },
      onError: (error: any) => {
        setToastMessage(error.message || "Error al guardar documentos");
        setToastVariant("danger");
        setShowToast(true);
      },
    }
  );

  const saveDocuments = saveDocumentsMutation.mutateAsync;

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === "string") resolve(reader.result.split(",")[1]);
        else reject("Error leyendo archivo");
      };
      reader.onerror = (error) => reject(error);
    });

  const resetFileInput = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (input) input.value = "";
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof FormData
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const base64 = await fileToBase64(files[0]);
      await uploadFile({
        file: {
          name: files[0].name,
          base64URL: base64,
        },
        fieldName,
      });
      resetFileInput(e.target.id);
    } catch {
      setToastMessage("Error al procesar el archivo");
      setToastVariant("danger");
      setShowToast(true);
    }
  };

  const onSubmit = async (data: FormData) => {
    let hasError = false;

    if (!data.identityDocument && !identityFileName) {
      setError("identityDocument", { message: "Debe subir el documento de identidad." });
      hasError = true;
    }



    if (!data.bankCertificate && !bankCertificateFileName) {
      setError("bankCertificate", { message: "Debe subir la certificación bancaria." });
      hasError = true;
    }

    if (hasError) return;

    try {
      await saveDocuments({
        identityDocument:
          data.identityDocument || identityFileName,
        rutDocument:
          data.rutDocument || rutFileName,
        bankCertificate:
          data.bankCertificate || bankCertificateFileName,
      });
      setUser({
        ...user!,
        identityDocument: data.identityDocument || identityFileName,
        rutDocument: data.rutDocument || rutFileName,
        bankCertificate: data.bankCertificate || bankCertificateFileName,
      })
    } catch { }
  };

  return (
    <>
      <div className="bg-white p-4 rounded-3 mb-4">
        <NavList />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="mb-4">
            <label className="form-label fw-semibold" htmlFor="identityDocumentFile">
              Documento de identidad (PDF) - Ampliado al 150
            </label>
            {identityFileName && (
              <div className="mb-1">
                <a
                  href={`${API_BASE_URL}/product/countries-available/get-file/${identityFileName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary small text-decoration-underline d-inline-flex align-items-center"
                >
                  Documento de identidad
                </a>
              </div>
            )}
            <input
              id="identityDocumentFile"
              type="file"
              accept="application/pdf"
              className={`form-control ${errors.identityDocument ? "is-invalid" : ""}`}
              onChange={(e) => handleFileChange(e, "identityDocument")}
            />
            {errors.identityDocument && (
              <div className="invalid-feedback">{errors.identityDocument.message}</div>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold" htmlFor="rutDocumentFile">
              Documento RUT (PDF)
            </label>
            {rutFileName && (
              <div className="mb-1">
                <a
                  href={`${API_BASE_URL}/product/countries-available/get-file/${rutFileName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary small text-decoration-underline d-inline-flex align-items-center"
                >
                  Documento RUT
                </a>
              </div>
            )}
            <input
              id="rutDocumentFile"
              type="file"
              accept="application/pdf"
              className={`form-control ${errors.rutDocument ? "is-invalid" : ""}`}
              onChange={(e) => handleFileChange(e, "rutDocument")}
            />
            {errors.rutDocument && (
              <div className="invalid-feedback">{errors.rutDocument.message}</div>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold" htmlFor="bankCertificateFile">
              Certificación bancaria (PDF)
            </label>
            {bankCertificateFileName && (
              <div className="mb-1">
                <a
                  href={`${API_BASE_URL}/product/countries-available/get-file/${bankCertificateFileName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary small text-decoration-underline d-inline-flex align-items-center"
                >
                  Certificación bancaria
                </a>
              </div>
            )}
            <input
              id="bankCertificateFile"
              type="file"
              accept="application/pdf"
              className={`form-control ${errors.bankCertificate ? "is-invalid" : ""}`}
              onChange={(e) => handleFileChange(e, "bankCertificate")}
            />
            {errors.bankCertificate && (
              <div className="invalid-feedback">{errors.bankCertificate.message}</div>
            )}
          </div>

          <button type="submit" className="btn btn-primary py-2 px-4 fw-semibold">
            Guardar documentos
          </button>
        </form>
      </div>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default Documents;
